export const APP_RELEASE_SCHEMA = 'automargin-app-release.v1'
export const APP_RELEASE_COOLDOWN_MS = 5 * 60 * 1000
const MAX_MANIFEST_BYTES = 2048

// Compare the two immutable assets already loaded by this document. A rollback
// is also a different app version, not necessarily a numerically newer release.
export function releaseFingerprint(manifest) {
  if (manifest?.schema !== APP_RELEASE_SCHEMA || !Array.isArray(manifest.assets) || manifest.assets.length !== 2) return null
  const assets = [...manifest.assets].sort()
  if (!assets.every(p => typeof p === 'string' && /^\/assets\/index-[a-zA-Z0-9_-]{6,80}\.(js|css)$/.test(p))) return null
  if (!assets.some(p => p.endsWith('.js')) || !assets.some(p => p.endsWith('.css'))) return null
  return assets.join('|')
}

export function documentRelease(doc) {
  try {
  const origin = new URL(doc.baseURI).origin
  const paths = [...doc.querySelectorAll('script[type="module"][src], link[rel="stylesheet"][href]')]
    .map(node => new URL(node.getAttribute('src') || node.getAttribute('href'), doc.baseURI))
    .filter(url => url.origin === origin && /^\/assets\/index-[a-zA-Z0-9_-]+\.(js|css)$/.test(url.pathname))
    .map(url => url.pathname)
  return releaseFingerprint({ schema: APP_RELEASE_SCHEMA, assets: paths })
  } catch { return null }
}

async function responseFingerprint(response) {
  if (!response.ok || !response.headers.get('content-type')?.toLowerCase().includes('application/json') || !response.body) return null
  const reader = response.body.getReader(), chunks = []
  let size = 0
  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      size += value.byteLength
      if (size > MAX_MANIFEST_BYTES) throw new Error('Oversized app release manifest')
      chunks.push(value)
    }
    const bytes = new Uint8Array(size)
    let offset = 0
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength }
    return releaseFingerprint(JSON.parse(new TextDecoder().decode(bytes)))
  } finally {
    await reader.cancel().catch(() => {})
    reader.releaseLock()
  }
}

// At most one small same-origin request per five minutes, on entry/focus only.
// No timers poll the server, and no listing/source refresh or persistent writes.
export function createAppReleaseCheck(current, { fetchImpl = globalThis.fetch, now = Date.now, timeoutMs = 5000 } = {}) {
  let lastAttempt = -Infinity, pending = null, stopped = false, controller = null
  let result = { status: 'unknown', fingerprint: null }
  return {
    check() {
      if (stopped || !current) return Promise.resolve({ status: 'unknown', fingerprint: null })
      if (pending) return pending
      const time = now()
      if (time >= lastAttempt && time - lastAttempt < APP_RELEASE_COOLDOWN_MS) return Promise.resolve(result)
      lastAttempt = time
      controller = new AbortController()
      const requestController = controller
      const timeout = setTimeout(() => requestController.abort(), timeoutMs)
      pending = Promise.resolve().then(async () => {
        try {
          const response = await fetchImpl('/app-release.json', { cache: 'no-store', credentials: 'omit', redirect: 'error', signal: requestController.signal })
          const fingerprint = await responseFingerprint(response)
          if (!stopped && !requestController.signal.aborted) result = { status: fingerprint ? (fingerprint === current ? 'same' : 'changed') : 'unknown', fingerprint }
        } catch { if (!stopped) result = { status: 'unknown', fingerprint: null } }
        finally { clearTimeout(timeout); requestController.abort(); pending = null }
        return stopped ? { status: 'unknown', fingerprint: null } : result
      })
      return pending
    },
    stop() { stopped = true; controller?.abort() },
  }
}
